// Die Übersicht – Manuels Startseite, auch fürs Büro (E3, S2; Mockup
// /crm/start; Gernots Entscheidung 3: „Top-Qualität, kein Schein“; O58).
// Alles kommt aus EINER Antwort des Servers (GET /api/crm/uebersicht), der
// jede Zahl in der Datenbank zählt (server/src/crm/uebersicht.ts) – hier
// wird nichts geschätzt und nichts dazugerechnet. Was 0 ist, zeigt 0; was
// leer ist, bleibt leer.
//
// Von oben nach unten:
//   - die Kacheln: zu tun (Lime, ein Klick: „Zu tun“ – seit E3c, S2 an Stelle
//     von „zu prüfen“; darunter, wie viele auf die Freigabe warten), bitte
//     ansehen, draußen in Aufnahme, offene Fragen, freigegeben diese Woche,
//     wartet am längsten seit …;
//   - der Zähler „verkauft – montiert – Ziel des Jahres“ als eigene Kachel
//     mit Balken, ehrlich beschriftet (von Hand gesetzt, bis die Kisten
//     Sales und Montage zählen); das Ziel setzt nur die Leitung;
//   - gesendet und freigegeben je Woche (Balken in SVG, ohne Bibliothek) und
//     je Monat (Tabelle), der Durchlauf und die Nachträge;
//   - „Der Weg der Anlagen“: je Kiste die Zahl und die jüngsten Karten;
//     Kisten, die noch nicht gebaut sind, gestrichelt mit „kommt später“;
//     alle acht nebeneinander, am Laptop ohne Scrollen. Seit E3b (O70) sind
//     Termin und Dachplanung Aufgaben-Spalten: oben „fehlt bei …“ mit
//     „Termin eintragen“ bzw. „Dachplanung anhängen“ an jeder Karte, darunter
//     „erledigt bei n“ – erledigt ist, was ohnehin an der Anlage steht.
//     Seit E3c, S2 setzt der Server die Kiste jeder Anlage aus ihrer Kette –
//     nach dem Freigeben die Bestellung, dann die Montage, montiert die
//     Inbetriebnahme.
// Die Rechnung dazu (Achse, Wochen, Worte) steht ohne React in
// uebersicht-zahlen.ts, mit Tests.

import { useEffect, useRef, useState, type FormEvent, type RefObject } from "react";
import { createPortal } from "react-dom";
import {
  darf,
  type AufgabenSpalte as AufgabenDaten,
  type BueroAntwort,
  type JahresZaehler,
  type JahreszielEingabe,
  type KisteMitAnlagen,
  type KistenKarte,
  type MonatsZahl,
  type UebersichtAntwort,
  type WochenZahl,
} from "@happy-energy/kern";
import { aendern, Chip, Ikon, senden, Spaeter, Tipp, toastZeigen } from "@happy-energy/oberflaeche";
import { dachplanungAnhaengen } from "./dachplanung.ts";
import { heuteInWien, standArt, zeitpunktZeigen } from "./anzeige.ts";
import { StandChip } from "./bausteine.tsx";
import { kisteFinden } from "./kisten.ts";
import { fehlerText, Laedt, LadeFehler, useLaden } from "./laden.tsx";
import { Seite, SucheSpaeter } from "./rahmen.tsx";
import {
  durchlaufText,
  hoeheProzent,
  monatBeschriften,
  skala,
  terminAufKarte,
  wartezeit,
  wocheBeschriften,
  zaehlerAnteile,
  zielLesen,
} from "./uebersicht-zahlen.ts";
import { useCrm } from "./zusammenhang.tsx";
import "./uebersicht.css";

// --- Die Kacheln ----------------------------------------------------------------------------

function Kacheln({ u }: { u: UebersichtAntwort }) {
  const { ich } = useCrm();
  const z = u.zahlen;
  const dieseWoche = u.jeWoche[u.jeWoche.length - 1];
  const fragenErlaubt = darf(ich.nutzer.rolle, "fragen");
  const fragenInhalt = (
    <>
      <span className="titel">Offene Fragen</span>
      <span className="wert">{z.offeneFragen}</span>
      <span className="klein">an allen Besichtigungen</span>
    </>
  );
  return (
    <div className="kpi-raster uebersicht-kacheln">
      <a href="#/zu-tun" className="kpi lime">
        <span className="titel">Zu tun</span>
        <span className="wert">{z.zuTun}</span>
        <span className="klein">
          {z.zuTun === 1 ? "Anlage" : "Anlagen"} mit einem Schritt, der jetzt geht – davon {z.zuPruefen} {z.zuPruefen === 1 ? "wartet" : "warten"} auf die Freigabe
        </span>
      </a>
      <a href="#/zu-tun" className={`kpi${z.bitteAnsehen > 0 ? " warn" : ""}`}>
        <span className="titel">Bitte ansehen</span>
        <span className="wert">{z.bitteAnsehen}</span>
        <span className="klein">nach der Freigabe kam etwas dazu</span>
      </a>
      <div className="kpi">
        <span className="titel">Draußen in Aufnahme</span>
        <span className="wert">{z.inAufnahme}</span>
        <span className="klein">begonnen, noch nicht gesendet</span>
      </div>
      {fragenErlaubt ? (
        <a href="#/fragen" className={`kpi${z.offeneFragen > 0 ? " kpi-frage" : ""}`}>
          {fragenInhalt}
        </a>
      ) : (
        <div className="kpi">{fragenInhalt}</div>
      )}
      <div className="kpi dunkel">
        <span className="titel">Freigegeben diese Woche</span>
        <span className="wert">{dieseWoche?.freigegeben ?? 0}</span>
        <span className="klein">Materiallisten fertig{dieseWoche ? ` · ${wocheBeschriften(dieseWoche.wocheAb).kurz}` : ""}</span>
      </div>
      {/* Seit dem Augentest (Gernot, 21.10.): „Wartet am längsten“ allein war unklar – jetzt mit Satz, was wartet und worauf. */}
      {z.wartetLaengstSeit ? (
        <a href="#/zu-tun" className="kpi">
          <span className="titel">Längste Wartezeit</span>
          <span className="wert">{wartezeit(z.wartetLaengstSeit, u.stand)}</span>
          <span className="klein">so lange wartet die älteste Besichtigung auf die Freigabe · gesendet {zeitpunktZeigen(z.wartetLaengstSeit)}</span>
        </a>
      ) : (
        <div className="kpi">
          <span className="titel">Längste Wartezeit</span>
          <span className="wert">–</span>
          <span className="klein">Gerade wartet keine Besichtigung auf die Freigabe.</span>
        </div>
      )}
    </div>
  );
}

// --- Der Zähler „verkauft – montiert – Ziel“ (O58) ------------------------------------------------

/** Das Ziel setzen – nur die Leitung (Recht „einstellungen“). */
function ZielSetzen({ z, fertig }: { z: JahresZaehler; fertig: () => void }) {
  const [offen, setOffen] = useState(false);
  const [text, setText] = useState("");
  const [meldung, setMeldung] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    const anlagen = zielLesen(text);
    if (anlagen === null) {
      setMeldung("Bitte eine ganze Zahl Anlagen eingeben, zum Beispiel 200.");
      return;
    }
    setSendet(true);
    try {
      await senden<BueroAntwort>("/api/crm/firma/jahresziel", { jahr: z.jahr, anlagen } satisfies JahreszielEingabe);
      toastZeigen(`Ziel für ${z.jahr}: ${anlagen} Anlagen`);
      setOffen(false);
      fertig();
    } catch (f) {
      setMeldung(fehlerText(f));
    } finally {
      setSendet(false);
    }
  }

  if (!offen) {
    return (
      <button
        type="button"
        className="crm-knopf rand klein zaehler-ziel-knopf"
        onClick={() => {
          setText(z.ziel === null ? "" : String(z.ziel));
          setMeldung(null);
          setOffen(true);
        }}
      >
        <Ikon name="ziel" groesse="k" /> {z.ziel === null ? "Ziel setzen" : "Ziel ändern"}
      </button>
    );
  }
  return (
    <form className="zaehler-ziel-form" onSubmit={(e) => void speichern(e)}>
      <label>
        Ziel für {z.jahr} – wie viele Anlagen?
        <input
          type="text"
          inputMode="numeric"
          autoFocus
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setMeldung(null);
          }}
          aria-invalid={meldung !== null}
        />
      </label>
      <div className="zaehler-ziel-knoepfe">
        <button type="submit" className="crm-knopf lime klein" disabled={sendet}>
          {sendet ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="haken" groesse="k" />} Speichern
        </button>
        <button type="button" className="crm-knopf rand klein" onClick={() => setOffen(false)} disabled={sendet}>
          Abbrechen
        </button>
      </div>
      {meldung && (
        <p className="zaehler-ziel-meldung" role="alert">
          {meldung}
        </p>
      )}
    </form>
  );
}

function ZaehlerBalken({ name, wert, ziel, art }: { name: string; wert: number; ziel: number; art: "verkauft" | "montiert" }) {
  const prozent = Math.round((wert / ziel) * 100);
  return (
    <div className="zaehler-balken">
      <span className="zaehler-balken-name">{name}</span>
      <div className="zaehler-spur" role="img" aria-label={`${name}: ${wert} von ${ziel}, ${prozent} Prozent`}>
        <i className={art} style={{ width: `${hoeheProzent(wert, ziel)}%` }} />
      </div>
      <span className="zaehler-balken-zahl">{prozent} %</span>
    </div>
  );
}

function ZaehlerKachel({ z, neuLaden }: { z: JahresZaehler; neuLaden: () => void }) {
  const { ich } = useCrm();
  const anteile = zaehlerAnteile(z);
  const darfZiel = darf(ich.nutzer.rolle, "einstellungen");
  return (
    <section className="zaehler-kachel" aria-label={`Der Zähler ${z.jahr}: verkauft, montiert, Ziel`}>
      <div className="zaehler-kopf">
        <div>
          <span className="zaehler-titel">Der Zähler {z.jahr}</span>
          <b>verkauft – montiert – Ziel des Jahres</b>
        </div>
        {darfZiel && <ZielSetzen z={z} fertig={neuLaden} />}
      </div>
      <dl className="zaehler-zahlen">
        <div>
          <dt>verkauft</dt>
          <dd>{z.verkauft}</dd>
        </div>
        <div>
          <dt>montiert</dt>
          <dd>{z.montiert}</dd>
        </div>
        <div className={z.ziel === null ? "ohne-ziel" : undefined}>
          <dt>Ziel</dt>
          <dd>{z.ziel ?? "–"}</dd>
        </div>
      </dl>
      {anteile && z.ziel !== null ? (
        <div className="zaehler-balken-liste">
          <ZaehlerBalken name="verkauft" wert={z.verkauft} ziel={z.ziel} art="verkauft" />
          <ZaehlerBalken name="montiert" wert={z.montiert} ziel={z.ziel} art="montiert" />
        </div>
      ) : (
        <p className="zaehler-ohne-ziel">
          <Ikon name="ziel" groesse="k" /> Ziel noch nicht gesetzt{darfZiel ? "" : " – das setzt die Leitung"}.
        </p>
      )}
      <p className="zaehler-ehrlich">
        Von Hand gesetzt, bis die Kisten Sales und Montage es selbst zählen: „Auftrag am“ und „montiert am“ trägt das Büro in der Akte der
        Anlage ein.
      </p>
    </section>
  );
}

// --- Gesendet und freigegeben je Woche und Monat ------------------------------------------------------

/** Die Maße des Diagramms (in Einheiten der viewBox – das SVG wächst mit der Breite). */
const D = { breite: 640, hoehe: 236, links: 34, rechts: 6, oben: 22, unten: 46, balken: 22, luecke: 3 } as const;

/** Ein Balken mit gerundeten oberen Ecken, unten gerade auf der Grundlinie. */
function balkenPfad(x: number, y: number, breite: number, hoehe: number): string {
  const r = Math.min(4, hoehe, breite / 2);
  const unten = y + hoehe;
  return `M${x},${unten} V${y + r} Q${x},${y} ${x + r},${y} H${x + breite - r} Q${x + breite},${y} ${x + breite},${y + r} V${unten} Z`;
}

function WochenDiagramm({ wochen }: { wochen: readonly WochenZahl[] }) {
  const s = skala(wochen.flatMap((w) => [w.gesendet, w.freigegeben]));
  const flaecheH = D.hoehe - D.oben - D.unten;
  const flaecheB = D.breite - D.links - D.rechts;
  const gruppe = flaecheB / Math.max(1, wochen.length);
  const grund = D.oben + flaecheH;
  const y = (wert: number) => grund - (hoeheProzent(wert, s.hoechst) / 100) * flaecheH;
  const beschreibung = wochen
    .map((w) => `${wocheBeschriften(w.wocheAb).kurz}: ${w.gesendet} gesendet, ${w.freigegeben} freigegeben`)
    .join("; ");

  return (
    <svg className="wochen-diagramm" viewBox={`0 0 ${D.breite} ${D.hoehe}`} role="img" aria-labelledby="wochen-titel wochen-beschreibung">
      <title id="wochen-titel">{`Gesendet und freigegeben je Woche – die letzten ${wochen.length} Wochen`}</title>
      <desc id="wochen-beschreibung">{beschreibung}</desc>
      {s.striche.map((strich) => (
        <g key={strich} className="achse">
          <line x1={D.links} x2={D.breite - D.rechts} y1={y(strich)} y2={y(strich)} className={strich === 0 ? "grundlinie" : "gitter"} />
          <text x={D.links - 7} y={y(strich) + 4} textAnchor="end">
            {strich}
          </text>
        </g>
      ))}
      {wochen.map((w, i) => {
        const links = D.links + i * gruppe;
        const x = links + (gruppe - (2 * D.balken + D.luecke)) / 2;
        const name = wocheBeschriften(w.wocheAb);
        const laufend = i === wochen.length - 1;
        const balken = [
          { art: "gesendet", wert: w.gesendet, x },
          { art: "freigegeben", wert: w.freigegeben, x: x + D.balken + D.luecke },
        ] as const;
        return (
          <g key={w.wocheAb} className={`woche${laufend ? " laufend" : ""}`}>
            <title>{`${name.kurz} (${name.tage}${laufend ? ", diese Woche" : ""}): ${w.gesendet} gesendet, ${w.freigegeben} freigegeben`}</title>
            <rect className="woche-flaeche" x={links + 2} y={D.oben - 18} width={gruppe - 4} height={flaecheH + 18 + D.unten - 4} rx={8} />
            {balken.map((b) =>
              b.wert > 0 ? <path key={b.art} className={`balken ${b.art}`} d={balkenPfad(b.x, y(b.wert), D.balken, grund - y(b.wert))} /> : null,
            )}
            {balken.map((b) => (
              <text key={`${b.art}-zahl`} className={`balken-zahl${b.wert === 0 ? " null" : ""}`} x={b.x + D.balken / 2} y={y(b.wert) - 5} textAnchor="middle">
                {b.wert}
              </text>
            ))}
            <text className="woche-name" x={links + gruppe / 2} y={grund + 17} textAnchor="middle">
              {laufend ? "diese Woche" : name.kurz}
            </text>
            <text className="woche-tage" x={links + gruppe / 2} y={grund + 32} textAnchor="middle">
              {name.ab}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function MonatsTabelle({ monate }: { monate: readonly MonatsZahl[] }) {
  return (
    <table className="monats-tabelle">
      <caption>Je Monat</caption>
      <thead>
        <tr>
          <th scope="col">Monat</th>
          <th scope="col">gesendet</th>
          <th scope="col">freigegeben</th>
        </tr>
      </thead>
      <tbody>
        {monate.map((m, i) => (
          <tr key={m.monat} className={i === monate.length - 1 ? "laufend" : undefined}>
            <th scope="row">
              {monatBeschriften(m.monat)}
              {i === monate.length - 1 && <span className="leise"> · bis heute</span>}
            </th>
            <td>{m.gesendet}</td>
            <td>{m.freigegeben}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Zeitreihe({ u }: { u: UebersichtAntwort }) {
  const durchlauf = durchlaufText(u.durchlauf);
  const n = u.zahlen.nachtraege30Tage;
  return (
    <section className="crm-karte zeitreihe" aria-label="Gesendet und freigegeben je Woche und Monat">
      <div className="zeitreihe-kopf">
        <h2>Gesendet und freigegeben</h2>
        <div className="diagramm-legende" aria-hidden="true">
          <span>
            <i className="gesendet" /> gesendet – vom Verkauf ans Büro
          </span>
          <span>
            <i className="freigegeben" /> freigegeben – die Materialliste steht
          </span>
        </div>
      </div>
      <div className="zeitreihe-raster">
        <div className="zeitreihe-wochen">
          <WochenDiagramm wochen={u.jeWoche} />
        </div>
        <MonatsTabelle monate={u.jeMonat} />
      </div>
      <div className="zeitreihe-fuss">
        <div>
          <span className="zeitreihe-titel">Von „gesendet“ bis „freigegeben“</span>
          <b>{durchlauf.wert}</b>
          <span className="leise">{u.durchlauf.anzahl > 0 ? `(${durchlauf.woraus})` : durchlauf.woraus}</span>
        </div>
        <div>
          <span className="zeitreihe-titel">Nachträge</span>
          <b>{n === 1 ? "1 Nachtrag" : `${n} Nachträge`}</b>
          <span className="leise">in den letzten 30 Tagen im Büro angekommen</span>
        </div>
      </div>
    </section>
  );
}

// --- Der Weg der Anlagen --------------------------------------------------------------------------------

/**
 * In diesen Kisten zählt der Montagetermin – dort steht er auf der Karte
 * (Gernot beim Augentest, 21.10.; seit E3c Bestellung statt Lager, seit S2
 * auch die Inbetriebnahme: dorthin wandert eine Anlage, wenn sie montiert ist –
 * die Karte sagt „montiert am …“).
 */
const KISTEN_MIT_TERMIN = new Set(["bestellung", "montage", "inbetriebnahme"]);

function WegKarte({ k, mitTermin, heute }: { k: KistenKarte; mitTermin: boolean; heute: string }) {
  // Mit gesendeter Besichtigung: dorthin, wo das Büro sie sieht; sonst die Akte der Anlage.
  const ziel = k.aufnahmeId ? `#/freigabe/${k.aufnahmeId}` : `#/anlage/${k.anlageId}`;
  const termin = mitTermin ? terminAufKarte(k, heute) : null;
  return (
    <a href={ziel} className="weg-karte">
      <b>{k.kundeName}</b>
      <span className="ort">{k.ort}</span>
      {termin && (
        <span className={`weg-termin ${termin.art}`}>
          <Ikon name={termin.art === "montiert" ? "haken" : "kalender"} groesse="k" />
          <span>
            {termin.zeile}
            {termin.text && <span className="weg-termin-text">{termin.text}</span>}
          </span>
        </span>
      )}
      <StandChip stand={standArt({ stand: k.stand, hinweis: k.hinweis }, true)} />
    </a>
  );
}

/**
 * Eine Kiste im Weg. Seit dem 22.10.2026 (Gernot: „das verwirrt“) ohne die
 * Bau-Wörter „Stufe 1“ und „Teil“ – eine Kiste, die erst später kommt, ist
 * gestrichelt und sagt „kommt später“; die anderen tragen keinen Chip.
 */
/**
 * Feste Trennstellen für die langen Namen der Kisten – in den schmalen Spalten
 * brach „Dachplanung“ sonst als „Dachplanun / g“ (Vorschau, 22.10.2026). Ein
 * weiches Trennzeichen (U+00AD) zeigt den Strich nur, wenn wirklich getrennt wird.
 */
const TRENNSTELLEN: Record<string, string> = { Dachplanung: "Dach\u00ADplanung", Inbetriebnahme: "Inbetrieb\u00ADnahme", Nachsorge: "Nach\u00ADsorge" };
function mitTrennstellen(name: string): string {
  return TRENNSTELLEN[name] ?? name;
}

function WegSpalte({ kiste, heute }: { kiste: KisteMitAnlagen; heute: string }) {
  const k = kisteFinden(kiste.kiste);
  const spaeter = k?.stufe === "spaeter";
  const name = k?.name ?? kiste.kiste;
  const mehr = kiste.anzahl - kiste.karten.length;
  return (
    <section className={`weg-spalte${spaeter ? " sp" : ""}`} aria-label={`${name}: ${kiste.anzahl} ${kiste.anzahl === 1 ? "Anlage" : "Anlagen"}`}>
      <div className="kopf">
        <div className="weg-kopf-zeile">
          <b>{mitTrennstellen(name)}</b>
          <span className={`weg-anzahl${kiste.anzahl === 0 ? " null" : ""}`} title={`${kiste.anzahl} ${kiste.anzahl === 1 ? "Anlage" : "Anlagen"} gerade hier`}>
            {kiste.anzahl}
          </span>
        </div>
        {k && <span className="wer">{k.wer}</span>}
        {spaeter && <Chip art="sp">kommt später</Chip>}
      </div>
      {kiste.karten.map((karte) => (
        <WegKarte key={karte.anlageId} k={karte} mitTermin={KISTEN_MIT_TERMIN.has(kiste.kiste)} heute={heute} />
      ))}
      {mehr > 0 && <p className="weg-mehr">und {mehr === 1 ? "1 weitere" : `${mehr} weitere`}</p>}
      {kiste.anzahl === 0 && <p className="weg-leer">{spaeter ? "Läuft bis dahin wie heute." : "Gerade nichts."}</p>}
    </section>
  );
}

/**
 * Wohin ein Fenster über der Seite gehört: in die Hülle des CRM (dort gelten
 * die Knopf- und Feld-Stile), nicht in die Übersicht – die ist ein
 * Container für ihre Spalten, und darin säße ein festes Fenster nicht über
 * der ganzen Seite, sondern nur über der Übersicht.
 */
function fensterZiel(von: HTMLElement): HTMLElement {
  return von.closest<HTMLElement>(".crm-huelle") ?? document.body;
}

/**
 * „Eintragen“ an einer Karte der Kiste Termin (Gernots Idee, 22.10.2026). Seit
 * dem Augentest E3b (Gernot, 23.10.: „zusammengequetscht, nicht hochwertig“)
 * kurz und in einer Zeile – die Karte sagt darüber schon „kein Termin“ –, und
 * der Termin kommt in ein eigenes Fenster statt in die schmale Karte.
 */
function TerminEintragen({ k, fertig }: { k: KistenKarte; fertig: () => void }) {
  // Offen: wohin das Fenster gehört (beim Klick bestimmt); zu: null.
  const [ziel, setZiel] = useState<HTMLElement | null>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button
        ref={knopf}
        type="button"
        className="crm-knopf rand klein aufgabe-knopf"
        aria-haspopup="dialog"
        aria-label={`Termin eintragen – ${k.kundeName}`}
        title={`Montagetermin bei ${k.kundeName} eintragen`}
        onClick={(e) => setZiel(fensterZiel(e.currentTarget))}
      >
        <Ikon name="kalender" groesse="k" /> Eintragen
      </button>
      {ziel &&
        createPortal(
          <TerminFenster
            k={k}
            zu={() => {
              setZiel(null);
              knopf.current?.focus();
            }}
            gespeichert={() => {
              // Zu, auch wenn das Neuladen danach scheitert – die Karte verschwindet mit dem nächsten Abgleich (Code-Prüfung E3b, Befund 5).
              setZiel(null);
              fertig();
            }}
          />,
          ziel,
        )}
    </>
  );
}

/** Das Fenster „Montagetermin“: Tag und ein paar Worte, „Speichern“ – Escape oder „Abbrechen“ schließen, ohne zu speichern. */
function TerminFenster({ k, zu, gespeichert }: { k: KistenKarte; zu: () => void; gespeichert: () => void }) {
  const [am, setAm] = useState("");
  const [text, setText] = useState("");
  const [sendet, setSendet] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const titel = `termin-titel-${k.anlageId}`;
  // Escape schließt – nicht, während gespeichert wird; die Ref, damit der Hörer nicht bei jedem Tippen neu hängt.
  const schliessen = useRef<() => void>(zu);
  const laeuft = useRef(false);
  useEffect(() => {
    schliessen.current = zu;
  });
  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !laeuft.current) schliessen.current();
    };
    document.addEventListener("keydown", taste);
    return () => document.removeEventListener("keydown", taste);
  }, []);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    if (!am && !text.trim()) {
      setMeldung("Bitte einen Tag oder ein paar Worte.");
      return;
    }
    laeuft.current = true;
    setSendet(true);
    setMeldung(null);
    try {
      await aendern<BueroAntwort>(`/api/crm/anlage/${k.anlageId}`, { montagetermin: { am: am || null, text: text.trim() || null } });
      toastZeigen(`Termin bei ${k.kundeName} eingetragen`);
      gespeichert();
    } catch (f) {
      setMeldung(fehlerText(f));
    } finally {
      laeuft.current = false;
      setSendet(false);
    }
  }

  return (
    <div
      className="nachfrage-huelle crm-nachfrage"
      onClick={(e) => {
        if (e.target === e.currentTarget && !sendet) zu();
      }}
    >
      <form className="nachfrage-fenster termin-fenster" role="dialog" aria-modal="true" aria-labelledby={titel} onSubmit={(e) => void speichern(e)}>
        <span className="nachfrage-zeichen">
          <Ikon name="kalender" groesse="g" />
        </span>
        <h2 id={titel}>Montagetermin</h2>
        <p className="termin-fenster-wer">
          <b>{k.kundeName}</b>
          {k.ort && <span> · {k.ort}</span>}
        </p>
        <div className="nachfrage-text">
          <p>Mit dem Kunden vereinbart – er steht danach in der Akte und auf der Unterlage für die Montage.</p>
        </div>
        <div className="termin-fenster-felder">
          <label className="crm-feld">
            Tag
            <input className="crm-eingabe" type="date" value={am} onChange={(e) => setAm(e.target.value)} autoFocus />
          </label>
          <label className="crm-feld">
            <span>
              Was dazu <span className="freiwillig">(freiwillig)</span>
            </span>
            <input className="crm-eingabe" value={text} maxLength={200} placeholder="z. B. Partie 2, 3 Tage" onChange={(e) => setText(e.target.value)} />
          </label>
        </div>
        {meldung && (
          <p className="crm-fehler-satz" role="alert">
            {meldung}
          </p>
        )}
        <div className="crm-nachfrage-knoepfe">
          <button type="button" className="crm-knopf rand" onClick={zu} disabled={sendet}>
            Abbrechen
          </button>
          <button type="submit" className="crm-knopf lime" disabled={sendet}>
            {sendet ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="haken" groesse="k" />} Speichern
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * „Hochladen“ an einer Karte der Kiste Dachplanung: das K2-PDF wählen – es hängt
 * danach an der Anlage, ohne „bitte ansehen“. Kurz und in einer Zeile wie
 * „Eintragen“ (Augentest E3b); die Karte sagt darüber schon „kein K2-PDF“. Bis
 * 3.11.2026 hieß der Knopf „Anhängen“ – leicht verwechselt mit dem Anhängen an
 * eine Mail (Gernots Augentest E3c).
 */
function DachplanungAnhaengen({ k, fertig }: { k: KistenKarte; fertig: () => void }) {
  const [sendet, setSendet] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);

  async function gewaehlt(datei: File | undefined) {
    if (!datei) return;
    setSendet(true);
    setMeldung(null);
    try {
      await dachplanungAnhaengen(k.anlageId, datei);
      toastZeigen(`Dachplanung bei ${k.kundeName} hochgeladen`);
      fertig();
    } catch (f) {
      setMeldung(f instanceof Error && !("status" in f) ? f.message : fehlerText(f));
    } finally {
      setSendet(false);
    }
  }

  return (
    <>
      <label className={`crm-knopf rand klein aufgabe-knopf${sendet ? " laedt" : ""}`} title={`K2-PDF bei ${k.kundeName} vom Computer ins CRM hochladen`}>
        {sendet ? <span className="kreisel" aria-hidden="true" /> : <Ikon name="hochladen" groesse="k" />} Hochladen
        <input
          type="file"
          accept="application/pdf,.pdf"
          className="datei-waehler"
          aria-label={`Dachplanung hochladen – ${k.kundeName}`}
          disabled={sendet}
          onChange={(e) => {
            void gewaehlt(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </label>
      {meldung && (
        <span className="aufgabe-meldung" role="alert">
          {meldung}
        </span>
      )}
    </>
  );
}

/**
 * Termin und Dachplanung als Aufgaben (seit E3b, O70): Durch diese zwei
 * Kisten „wandert“ keine Anlage – sie zeigen, wo etwas fehlt. Oben „fehlt bei
 * n“ mit den Karten (die am längsten fehlen zuerst) und dem Knopf an jeder,
 * darunter „erledigt bei n“. Erledigt ist, was ohnehin an der Anlage steht –
 * niemand muss etwas zusätzlich abhaken (Gernot, 22.10.2026).
 */
function AufgabenSpalte({ kiste, art, daten, neuLaden }: { kiste: string; art: "termin" | "dachplanung"; daten: AufgabenDaten; neuLaden: () => void }) {
  const k = kisteFinden(kiste);
  const name = k?.name ?? kiste;
  const mehr = daten.fehltAnzahl - daten.fehlt.length;
  return (
    <section className="weg-spalte aufgaben" aria-label={`${name}: fehlt bei ${daten.fehltAnzahl}, erledigt bei ${daten.erledigt}`}>
      <div className="kopf">
        <div className="weg-kopf-zeile">
          <b>{mitTrennstellen(name)}</b>
          <span className={`weg-anzahl${daten.fehltAnzahl === 0 ? " null" : " fehlt"}`} title={`fehlt bei ${daten.fehltAnzahl}`}>
            {daten.fehltAnzahl}
          </span>
        </div>
        {k && <span className="wer">{k.wer}</span>}
      </div>
      {daten.fehltAnzahl > 0 && <p className="aufgabe-satz">fehlt bei {daten.fehltAnzahl === 1 ? "1 Anlage" : `${daten.fehltAnzahl} Anlagen`}</p>}
      {daten.fehlt.map((karte) => (
        <div key={karte.anlageId} className="weg-karte aufgabe-karte">
          <a href={karte.aufnahmeId ? `#/freigabe/${karte.aufnahmeId}` : `#/anlage/${karte.anlageId}`} className="aufgabe-wer">
            <b>{karte.kundeName}</b>
            <span className="ort">{karte.ort}</span>
          </a>
          <span className="aufgabe-fehlt">
            <Ikon name="achtung" groesse="k" />{" "}
            {art === "termin" ? (
              "kein Termin"
            ) : (
              // Nicht am Bindestrich trennen – „K2-“ und „PDF“ auf zwei Zeilen las sich schlecht.
              <span>
                kein <span className="ohne-umbruch">K2-PDF</span>
              </span>
            )}
          </span>
          {art === "termin" ? <TerminEintragen k={karte} fertig={neuLaden} /> : <DachplanungAnhaengen k={karte} fertig={neuLaden} />}
        </div>
      ))}
      {mehr > 0 && <p className="weg-mehr">und {mehr === 1 ? "1 weitere" : `${mehr} weitere`}</p>}
      {daten.fehltAnzahl === 0 && <p className="weg-leer">{daten.erledigt > 0 ? "Überall erledigt." : "Gerade nichts."}</p>}
      {daten.erledigt > 0 && (
        <p className="aufgabe-erledigt">
          <Ikon name="haken" groesse="k" /> erledigt bei {daten.erledigt === 1 ? "1 Anlage" : `${daten.erledigt} Anlagen`}
        </p>
      )}
    </section>
  );
}

/**
 * Steht rechts vom sichtbaren Teil noch etwas? Nur dann, wenn das Fenster
 * zu schmal für alle acht Kisten ist – und nur, bis ganz nach rechts
 * gescrollt ist.
 */
function useRechtsMehr(rahmen: RefObject<HTMLDivElement | null>): boolean {
  const [mehr, setMehr] = useState(false);
  useEffect(() => {
    const el = rahmen.current;
    if (!el) return;
    const pruefen = () => setMehr(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    pruefen();
    el.addEventListener("scroll", pruefen, { passive: true });
    window.addEventListener("resize", pruefen);
    // Kommen Karten dazu oder gehen, ändert sich die Breite ohne Scrollen.
    const beobachter = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(pruefen);
    beobachter?.observe(el);
    if (el.firstElementChild) beobachter?.observe(el.firstElementChild);
    return () => {
      el.removeEventListener("scroll", pruefen);
      window.removeEventListener("resize", pruefen);
      beobachter?.disconnect();
    };
  }, [rahmen]);
  return mehr;
}

/**
 * Der Weg: alle acht Kisten nebeneinander – am Laptop bei 1280 px ohne
 * Scrollen (Gernot, 22.10.2026: Inbetriebnahme und Nachsorge fand er erst
 * beim waagrechten Scrollen). Ist das Fenster schmaler, darf der Weg
 * scrollen; dann zeigt ein Verlauf am rechten Rand „weitere Kisten →“, und
 * ein Klick darauf scrollt weiter.
 */
function Weg({
  kisten,
  aufgaben,
  heute,
  neuLaden,
}: {
  kisten: readonly KisteMitAnlagen[];
  aufgaben: UebersichtAntwort["aufgaben"] | undefined;
  heute: string;
  neuLaden: () => void;
}) {
  const rahmen = useRef<HTMLDivElement>(null);
  const mehr = useRechtsMehr(rahmen);
  return (
    <div className="weg-huelle">
      <div className="weg-rahmen" ref={rahmen} tabIndex={0} aria-label="Der Weg der Anlagen durch die acht Kisten">
        <div className="weg">
          {kisten.map((k) =>
            // Seit E3b: Termin und Dachplanung zeigen Aufgaben (ein Server mit altem Stand schickt sie nicht – dann wie vorher).
            (k.kiste === "termin" || k.kiste === "dachplanung") && aufgaben ? (
              <AufgabenSpalte key={k.kiste} kiste={k.kiste} art={k.kiste} daten={aufgaben[k.kiste]} neuLaden={neuLaden} />
            ) : (
              <WegSpalte key={k.kiste} kiste={k} heute={heute} />
            ),
          )}
        </div>
      </div>
      {mehr && (
        <button
          type="button"
          className="weg-weiter"
          onClick={() => {
            const el = rahmen.current;
            if (el) el.scrollBy({ left: Math.max(160, el.clientWidth * 0.8), behavior: "smooth" });
          }}
        >
          <span className="weg-weiter-text">
            weitere Kisten <Ikon name="rechts" groesse="k" />
          </span>
        </button>
      )}
    </div>
  );
}

// --- Die Seite -------------------------------------------------------------------------------------

/**
 * Die Zahlen frisch halten: jede Minute (nur, solange die Seite zu sehen ist)
 * und sobald das Fenster wieder nach vorne kommt. Ersetzt den Knopf „Neu
 * zählen“ – den hat Gernot beim Augentest am 21.10. nicht verstanden, und
 * nach Grundsatz 1 soll niemand ans Auffrischen denken müssen. Die alten
 * Zahlen bleiben stehen, bis die neuen da sind (useLaden).
 */
function useFrischHalten(neu: () => void): void {
  useEffect(() => {
    const vorne = () => document.visibilityState === "visible";
    const uhr = window.setInterval(() => {
      if (vorne()) neu();
    }, 60_000);
    const sichtbar = () => {
      if (vorne()) neu();
    };
    document.addEventListener("visibilitychange", sichtbar);
    return () => {
      window.clearInterval(uhr);
      document.removeEventListener("visibilitychange", sichtbar);
    };
  }, [neu]);
}

export function Uebersicht() {
  const { ich } = useCrm();
  const { daten: u, fehler, neu } = useLaden<UebersichtAntwort>("/api/crm/uebersicht");
  useFrischHalten(neu);
  const heute = new Date().toLocaleDateString("de-AT", { weekday: "long", day: "numeric", month: "long" });
  const gezaehlt = u ? ` · gezählt um ${new Date(u.stand).toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit" })}` : "";
  const zuTun = u?.zahlen.zuTun ?? 0;
  const leer = u !== undefined && u.kisten.every((k) => k.anzahl === 0);

  return (
    <Seite
      titel="Übersicht"
      unter={`${heute} · ${ich.firma.name}${gezaehlt}`}
      knoepfe={
        <>
          <SucheSpaeter />
          {zuTun > 0 && (
            <a href="#/zu-tun" className="crm-knopf lime klein">
              <Ikon name="liste" groesse="k" /> {zuTun} zu tun
            </a>
          )}
        </>
      }
    >
      {fehler !== undefined && <LadeFehler fehler={fehler} nochmal={neu} />}
      {!u && fehler === undefined && <Laedt text="Die Zahlen werden gezählt …" />}
      {u && (
        <div className="uebersicht">
          <div className="uebersicht-oben">
            <Kacheln u={u} />
            <ZaehlerKachel z={u.zaehler} neuLaden={neu} />
          </div>

          <Zeitreihe u={u} />

          {leer && <Tipp>Noch keine Anlage im System – sie erscheint hier, sobald der Verkauf draußen eine Besichtigung beginnt.</Tipp>}

          {/* Seit dem Augentest (Gernot, 21.10.: „verstehe ich nicht ganz, was da dargestellt wird“) mit einem Satz, was man sieht. Die
              Zeichen-Erklärung („Stufe 1“, „Teil“) ist seit dem 22.10. weg – die gestrichelten Kisten sagen selbst „kommt später“. */}
          <div className="weg-erklaerung">
            <div className="crm-abschnitt">
              <h2>Der Weg der Anlagen</h2>
            </div>
            <p className="weg-satz leise">
              Jede Spalte ist eine Kiste – eine Station im Leben einer Anlage, von der Anfrage links bis zur Nachsorge rechts. Die Zahl oben rechts sagt, wie
              viele Anlagen gerade dort stehen; darunter die jüngsten, ein Klick öffnet sie. Bei Termin und Dachplanung zählt die Zahl, wo es noch fehlt.
            </p>
          </div>
          <Weg kisten={u.kisten} aufgaben={u.aufgaben} heute={heuteInWien(new Date(u.stand))} neuLaden={neu} />
          <Spaeter titel="Transparenz auf jeder Ebene" wann="Stufe 3 und weiter">
            Ziele und Zähler je Kiste, für jede Rolle sichtbar.
          </Spaeter>
        </div>
      )}
    </Seite>
  );
}
